import { useState } from 'react';
import { doc, updateDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { logAdminAction } from '../../utils/auditLogger';

const AVAILABLE_COINS = [
  { value: 'RCN', label: 'RCN (Test)' },
  { value: 'FLCV', label: 'FLCV' },
  { value: 'PETCN', label: 'PETCN' },
  { value: 'FLCHU', label: 'FLCHU' },
  { value: 'TRLCN', label: 'TRLCN' },
  { value: 'SHPCN', label: 'SHPCN' },
  { value: 'VPVB', label: 'VPVB' },
  { value: 'GLDCN', label: 'GLDCN' },
  { value: 'CHRTC', label: 'CHRTC' },
  { value: 'HLTCN', label: 'HLTCN' }
];

export default function AddWalletBalance({ user, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('RCN');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const currentBalance = parseFloat(user.details?.balances?.[currency] || 0);
      const newBalance = currentBalance + parseFloat(amount);
      const amountValue = parseFloat(amount);

      // Frissítjük a felhasználó egyenlegét
      const userRef = doc(db, 'walletUsers', user.id);
      await updateDoc(userRef, {
        [`details.balances.${currency}`]: newBalance.toString(),
        last_updated: new Date()
      });

      // Létrehozunk egy tranzakciót is az egyenleg módosításról
      const transactionData = {
        transaction_id: `ADMIN_BALANCE_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        amount: `${amount} ${currency}`,
        sender: {
          email: 'admin@elonyklub.hu',
          user_id: 'admin'
        },
        recipient: {
          email: user.email,
          user_id: user.user_id
        },
        description: description || 'Admin egyenleg módosítás',
        created_at: new Date(),
        status: 'completed',
        type: amountValue >= 0 ? 'admin_deposit' : 'admin_withdrawal'
      };

      await addDoc(collection(db, 'walletTransactions'), transactionData);

      // Naplózzuk az egyenleg módosítást
      await logAdminAction('wallet.balance_modify', {
        userId: user.user_id,
        email: user.email,
        coin: currency,
        previousBalance: currentBalance.toString(),
        newBalance: newBalance.toString(),
        modification: amount,
        description: description || 'Admin egyenleg módosítás'
      });

      toast.success('Egyenleg sikeresen módosítva');
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error modifying balance:', error);
      toast.error('Hiba történt az egyenleg módosítása során');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Egyenleg Módosítása</h2>
          <p className="text-sm text-gray-500 mt-1">{user.email}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
          <Icons.X className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Jelenlegi Egyenlegek
          </label>
          <div className="bg-gray-50 p-5 rounded-lg grid grid-cols-2 gap-4">
            {AVAILABLE_COINS.map(coin => (
              <div key={coin.value} className="text-sm flex items-center">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center mr-2">
                  <Icons.Coins className="h-3 w-3 text-indigo-600" />
                </div>
                <div>
                  <span className="font-medium">{coin.label}:</span>{' '}
                  <span className="text-indigo-600 font-medium">{user.details?.balances?.[coin.value] || '0'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Coin
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            >
              {AVAILABLE_COINS.map(coin => (
                <option key={coin.value} value={coin.value}>
                  {coin.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mennyiség (+/-)
            </label>
            <div className="flex">
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-indigo-500 focus:border-indigo-500"
                required
                placeholder="Pl. 100 vagy -50"
              />
              <div className="bg-gray-100 px-4 py-3 flex items-center justify-center border border-l-0 border-gray-300 rounded-r-lg">
                <span className="text-gray-700 font-medium">{currency}</span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Megjegyzés
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            rows={3}
            placeholder="Adjon meg egy megjegyzést az egyenleg módosításhoz..."
          />
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center">
            <Icons.Info className="h-5 w-5 text-indigo-500 mr-2" />
            <p className="text-sm text-gray-600">
              {parseFloat(amount) >= 0 
                ? `Hozzáadás: ${amount} ${currency} - Új egyenleg: ${(parseFloat(user.details?.balances?.[currency] || 0) + parseFloat(amount || 0)).toFixed(2)} ${currency}`
                : `Levonás: ${amount} ${currency} - Új egyenleg: ${(parseFloat(user.details?.balances?.[currency] || 0) + parseFloat(amount || 0)).toFixed(2)} ${currency}`
              }
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Mégsem
          </button>
          <button
            type="submit"
            disabled={loading || !amount}
            className="px-6 py-3 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center"
          >
            {loading ? (
              <>
                <Icons.Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span>Feldolgozás...</span>
              </>
            ) : (
              <>
                <Icons.Save className="h-5 w-5 mr-2" />
                <span>Egyenleg Módosítása</span>
              </>
            )}
          </button>
        </div>
      </form>
    </>
  );
} 